import type { RowData } from '@tanstack/react-table';
import type { ParsedSuperViewData } from 'src/api/viewModels/data';
import type { DensityType } from 'src/types/store.types';
import type { AssetPermissions, CollectionItem } from '../types';

// TanStack Table module augmentation for type-safe column meta access
declare module '@tanstack/react-table' {
  // biome-ignore lint/correctness/noUnusedVariables: required by TanStack Table's module augmentation pattern
  interface ColumnMeta<TData extends RowData, TValue> {
    fieldLabel?: string;
    sequence?: number;
    slug?: string;
    name?: string;
    scope?: string[];
    isNameColumn?: boolean;
  }
}

// Row type discriminator
export type ListViewRowType = 'asset' | 'collection';

// Density configuration
export interface DensityConfig {
  cellPaddingY: number;
  cellPaddingX: number;
  rowMinHeight: number;
  fontSize: string;
  headerFontSize: string;
  thumbWidth: number;
  thumbHeight: number;
}

export const DENSITY_CONFIGS: Record<DensityType, DensityConfig> = {
  compact: {
    cellPaddingY: 2,
    cellPaddingX: 6,
    rowMinHeight: 28,
    fontSize: '0.75rem',
    headerFontSize: '0.625rem',
    thumbWidth: 0,
    thumbHeight: 0,
  },
  comfortable: {
    cellPaddingY: 8,
    cellPaddingX: 12,
    rowMinHeight: 44,
    fontSize: '0.8125rem',
    headerFontSize: '0.75rem',
    thumbWidth: 40,
    thumbHeight: 26,
  },
  spacious: {
    cellPaddingY: 14,
    cellPaddingX: 16,
    rowMinHeight: 60,
    fontSize: '0.875rem',
    headerFontSize: '0.8125rem',
    thumbWidth: 64,
    thumbHeight: 42,
  },
};

// Base row interface
interface ListViewRowBase {
  id: string;
  __rowType: ListViewRowType;
}

// Asset row data
export interface AssetListViewRow extends ListViewRowBase {
  __rowType: 'asset';
  itemId: string;
  name?: string;
  permissions?: AssetPermissions;
  highlights?: Record<string, string[]>;
}

// Collection row data
export interface CollectionListViewRow extends ListViewRowBase {
  __rowType: 'collection';
  collection: CollectionItem;
}

// Union type for all row types
export type ListViewRow = AssetListViewRow | CollectionListViewRow;

// Column metadata from ViewMediaCardListView (mirrors the ColumnMeta augmentation above)
export interface ListViewColumnMeta {
  fieldLabel: string;
  sequence: number;
  slug?: string;
  name?: string;
  scope?: string[];
  isNameColumn?: boolean;
}

// Column min-widths
export const COLUMN_MIN_WIDTHS = {
  select: 78,
  type: 52,
  thumbnailComfortable: 56,
  thumbnailSpacious: 80,
  name: 150,
  dynamic: 80,
  actions: 48,
} as const;

// Search highlight terms (computed once per page, passed to row components)
export interface HighlightTerms {
  searchTerm: string;
  hasWords: string[];
  hasPhrase: string;
}

// Row context value — provided by row wrappers, consumed by cell components
export interface ListViewRowContextValue {
  rowType: ListViewRowType;
  itemId?: string;
  permissions?: AssetPermissions;
  highlights?: Record<string, string[]>;
  fieldsBySlug: Map<string, ParsedSuperViewData>;
  loading: boolean;
  collection?: CollectionItem;
  highlightTerms: HighlightTerms;
  density: DensityConfig;
  classes: Record<string, string>;
  handleNavigate: (id: string) => void;
  onShowPreview?: () => void;
}
